
import React, { useState, useEffect, useRef } from 'react';

export const HowItWorks: React.FC = () => {
  const [activeStep, setActiveStep] = useState<number | null>(0);
  const [isHovering, setIsHovering] = useState(false);
  const cycleRef = useRef<number | null>(null);

  const steps = [
    {
      id: 0,
      label: "CONNECT",
      title: "Unify operational signals into a single, trusted execution foundation.",
      description: "Ingest structured and unstructured data across ERPs, APIs, and files using no-code pipelines and industry-mapped KPIs across cloud and edge."
    },
    {
      id: 1,
      label: "DETECT",
      title: "Detect risk, drift, and opportunity before outcomes are impacted.",
      description: "Auto-mapped KPIs and predictive intelligence reveal what’s changing, why it matters, and what could happen next."
    },
    {
      id: 2,
      label: "EXECUTE",
      title: "Turn intelligence into governed, accountable action.",
      description: "Trigger prescriptive actions with human approval, system write-back, and KPI-linked playbooks with full auditability and control."
    }
  ];

  // Auto-cycling logic - Reduced from 6000ms to 3500ms for snappier motion
  useEffect(() => {
    if (!isHovering) {
      cycleRef.current = window.setInterval(() => {
        setActiveStep((prev) => (prev === null || prev >= 2 ? 0 : prev + 1));
      }, 3500);
    } else {
      if (cycleRef.current) clearInterval(cycleRef.current);
    }
    return () => {
      if (cycleRef.current) clearInterval(cycleRef.current);
    };
  }, [isHovering]);

  const handleMouseEnter = (id: number) => {
    setIsHovering(true);
    setActiveStep(id);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <section id="platform" className="py-32 px-6 md:px-12 bg-brandBg border-t border-zodiac/5 overflow-hidden text-center">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-32 max-w-4xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.4em] text-accent mb-4">HOW GROFLEX WORKS</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-zodiac mb-8 tracking-tighter leading-tight">Make Critical Decisions in Minutes, Not Days</h2>
          <p className="text-xl text-zodiac/60 max-w-3xl font-light leading-relaxed mx-auto">
            Groflex governs the decision-to-action loop across enterprise systems without replacing what you already run.
          </p>
        </div>

        {/* The Visual Flow Stage */}
        <div className="relative pt-40 pb-24 lg:pb-32 px-4">
          
          {/* FEEDBACK LOOP ARC & FLOW ARROWS */}
          <div className="absolute top-0 left-0 w-full hidden lg:block pointer-events-none z-0">
            <svg viewBox="0 0 1000 260" className="w-full h-auto overflow-visible" fill="none">
              <defs>
                <filter id="homeSoftGlow" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
                </filter>
                
                <marker id="flowArrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#00A1E4" opacity="0.6" />
                </marker>
              </defs>

              {/* Dotted Learning Arc (Feedback Loop) */}
              <path 
                id="learningPath"
                d="M 835 180 C 835 40, 165 40, 165 180" 
                stroke="#00A1E4" 
                strokeWidth="1.5" 
                strokeDasharray="8 8"
                className="opacity-20"
              />

              {/* Horizontal Flow Arrows between Connect -> Detect -> Execute */}
              <path 
                d="M 260 180 L 415 180" 
                stroke="#00A1E4" 
                strokeWidth="1.5" 
                strokeDasharray="4 6" 
                opacity="0.3" 
                markerEnd="url(#flowArrowhead)"
              >
                <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.2s" repeatCount="indefinite" />
              </path>

              <path 
                d="M 585 180 L 740 180" 
                stroke="#00A1E4" 
                strokeWidth="1.5" 
                strokeDasharray="4 6" 
                opacity="0.3" 
                markerEnd="url(#flowArrowhead)"
              >
                <animate attributeName="stroke-dashoffset" from="20" to="0" dur="1.2s" repeatCount="indefinite" />
              </path>

              {/* Learning Particles */}
              {[0, 1, 2].map((i) => (
                <circle key={i} r="3" fill="#00A1E4" filter="url(#homeSoftGlow)">
                  <animateMotion dur="4s" begin={`${i * 1.3}s`} repeatCount="indefinite" path="M 835 180 C 835 40, 165 40, 165 180" />
                </circle>
              ))}

              <g className={`transition-all duration-700 ${activeStep === 2 ? 'opacity-100' : 'opacity-20'}`}>
                <ellipse cx="500" cy="45" rx="80" ry="25" fill="white" fillOpacity="0.8" filter="url(#homeSoftGlow)" />
                <text x="500" y="52" textAnchor="middle" className="fill-accent text-[18px] font-semibold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Learning
                </text>
              </g>
            </svg>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-20 lg:gap-12 relative z-10">
            {steps.map((step, idx) => {
              const isActive = activeStep === step.id;
              return (
                <div 
                  key={step.id}
                  className="relative flex flex-col items-center text-center group cursor-pointer"
                  onMouseEnter={() => handleMouseEnter(step.id)}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className={`
                    w-40 h-40 md:w-48 md:h-48 rounded-full border-2 flex flex-col items-center justify-center mb-10 bg-white relative
                    transition-all duration-500
                    ${isActive ? 'border-accent shadow-[0_0_80px_rgba(0,161,228,0.2)] scale-110' : 'border-zodiac/10 shadow-sm'}
                  `}>
                    {isActive && (
                      <div className="absolute inset-0 rounded-full border-2 border-accent animate-ping opacity-20"></div>
                    )}
                    <span className={`text-[9px] font-black tracking-[0.4em] mb-3 transition-colors duration-300 ${isActive ? 'text-accent' : 'text-zodiac/30'}`}>
                      {step.label}
                    </span>
                    <span className={`text-[11px] md:text-xs font-black uppercase tracking-widest leading-tight px-6 transition-colors duration-300 ${isActive ? 'text-zodiac' : 'text-zodiac/70'}`}>
                      {step.label === "CONNECT" ? "SIGNAL INGESTION" : step.label === "DETECT" ? "INTELLIGENCE HUB" : "GOVERNED ACTION"}
                    </span>
                  </div>
                  
                  <div className="max-w-[320px]">
                    <div className={`transition-all duration-500 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-40 translate-y-2'}`}>
                      <h4 className={`text-base font-bold mb-4 transition-colors duration-300 ${isActive ? 'text-zodiac' : 'text-zodiac/50'}`}>
                        {step.title}
                      </h4>
                      <p className={`text-sm leading-relaxed font-medium transition-colors duration-300 ${isActive ? 'text-zodiac/70' : 'text-zodiac/30'}`}>
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
